/*
  Classes used to represent logic to handle RateSchedules and RateScheduleCharge data.
*/
export class RateSchedule {
  constructor(data) {
    this.data = data 
    this.charges = []
    // const billing_setting_options = this.data.billing_setting_options
    this.data.rate_schedule_charges_attributes.map(charge => {
      this.charges.push(new RateScheduleCharge(charge))
    })
  }

  // returns array of charges that are considered to be shown in the included section.
  included_items(){
    return this.charges.filter(charge => {
      return charge.is_included_item()
    })
  }

    // returns array of charges that are considered to be shown in the additional section.
  additional_items(){
    return this.charges.filter(charge => {
      return charge.is_additional_usage_item()
    })
  }

  get_base_price_charge(){
    return this.charges.find(charge => {return charge.data.base_price === true})
  }

  get_base_price(){
    let base_price = 0
    const charge = this.get_base_price_charge()

    if (charge){
      base_price = charge.data.charge_periods_attributes[0].price
    }

    if (isNaN(base_price)){
      base_price = 0
    }
    return base_price
  }
}


export class RateScheduleCharge {
  constructor(data){
    this.data = data
    //console.log('RateScheduleCharge', data)
  }


  /*
    These functions are shared between components as they deal with rate_schedule_charge object data.
    Ideally we have a RateScheduleCharge class to encapsulate the charge data and these functions.
    Determine if the charge is to be listed as an included item
    for the billing plan. 
    2. if a charge has tier data and the first row of tier data has a low value and price of 0 and a high value > 0
    3. if the ui sets the destroy attribute, return false
    5. charge is the base_price for the billing plan return false
    6. If the charge period tier type is N, and the price is > 0, return true
  */
  is_included_item(){
    // 3
    if (this.data.destroy) { return false}
    // 5
    if (this.data.base_price) { return false}

    const included_items = this.data.charge_periods_attributes.filter(cp => {
      return this.is_an_included_item(cp)
    })

    return included_items.length > 0
  }

  // returns true if the passed charge_period is considered an included item
  is_an_included_item(cp) {
    let included = false

    if (this.data.charge_tier_type === 'N'){ 
      const price = parseFloat(cp.price)
      if (price > 0) {
        included = true
      }
    }
    else {
      if (cp.charge_period_tiers_attributes) {
        // 2
        // the first tier data is used to represent the included amount
        // so the low value and price are expected to be 0
        const low_amount = parseInt(cp.charge_period_tiers_attributes[0].tier_low_value)
        const high_amount = parseInt(cp.charge_period_tiers_attributes[0].tier_high_value)
        const price = parseFloat(cp.charge_period_tiers_attributes[0].price)
        if (low_amount === 0 && price === 0 && high_amount > 0) {
          included = true
        }
      }
    }
    
    return included  
  }
  
  get_formatted_included_amount() {
    const amount = this.get_included_amount()
    return numeral( amount ).format('0,0');
  }

  get_included_amount() {
    let amount = 0

    // we assume that no charge periods are allowed to overlap as that would not make sense.
    // so we need to find the charge period that contains today's date.
    // Later we may want to include a filter to see price changes over time.
    const cp = this.data.charge_periods_attributes.find(cp => {
      return moment().isBetween(cp.start_date, cp.end_date, null, '[]');
    })
    if (cp){
      amount = this.get_an_included_amount(cp)
    }
    
    return amount
  }

  get_an_included_amount(cp) {
    let amount = 0

    if (this.data.charge_tier_type === 'N'){ 
      amount = parseInt(cp.price)
    }
    else {
      if (cp.charge_period_tiers_attributes) {
        // 2
        // the first tier data is used to represent the included amount
        // so the low value and price are expected to be 0
        const low_amount = parseInt(cp.charge_period_tiers_attributes[0].tier_low_value)
        const high_amount = parseInt(cp.charge_period_tiers_attributes[0].tier_high_value)
        const price = parseFloat(cp.charge_period_tiers_attributes[0].price)
        if (low_amount === 0 && price === 0 && high_amount > 0) {
          amount = high_amount
        }
      }
    }

    return amount
  }

  set_included_usage_amount(v){
    v = parseInt(v)

    // we assume that no charge periods are allowed to overlap as that would not make sense.
    // so we need to find the charge period that contains today's date.
    // Later we may want to include a filter to see price changes over time.
    const cp = this.data.charge_periods_attributes.find(cp => {
      return moment().isBetween(cp.start_date, cp.end_date, null, '[]');
    })

    if (cp){
      if (this.data.charge_tier_type === 'N'){ 
        cp.price = v
      }
      else {
        if (cp.charge_period_tiers_attributes) {
          // 2
          // the first tier data is used to represent the included amount
          // so the low value and price are expected to be 0
          const low_amount = parseInt(cp.charge_period_tiers_attributes[0].tier_low_value)
          const price = parseFloat(cp.charge_period_tiers_attributes[0].price)
          if (low_amount === 0 && price === 0) {
            cp.charge_period_tiers_attributes[0].tier_high_value = v
          }
        }
      }
    }
  }    

  /*
    Determine which charges should be considered as part of the additional usage portion of a 
    subscription. These items will get dispayed in the ui for rate schedule / billin plan 
    when you click on the Additional Charges link and are shown under Additional Usage
    Rules to be part of included usage
    1. if a charge has tier data, use the price for the first row of tier data where
    the tier_low_value is > 0 and price is > 0.
    2. If no tier data, use the price if it is > 0.
    3. if the ui sets the destroy attribute, ignore the charge
  */
  is_additional_usage_item(){
    if (this.data.destroy) { return false}
    if (this.data.base_price) { return false}

    let additional_items = []
    if (this.data.charge_tier_type !== 'N') {
      additional_items = this.data.charge_periods_attributes.filter(cp => {
        return this.is_an_additional_usage_item(cp)
      })
    }

    return additional_items.length > 0
  }

  is_an_additional_usage_item(cp){
    let is_additional = false

    /*
      Initially just dealing with two tier levels
      tier 1 used to indicate if there are any included values for the billing plan like 5 DNI numbers
      tier 2 used to indicate the usage price
    */
    if (cp.charge_period_tiers_attributes){
      // the first tier data is used to represent the included amount
      // so the low value and price are expected to be 0
      // get the last tier record. Assumption is that this represents the additional usage
      const additional_item_tier = cp.charge_period_tiers_attributes.slice(-1)[0]
      const high_amount = parseInt(additional_item_tier.tier_high_value)
      const price = parseFloat(additional_item_tier.price)
      if (price > 0 && high_amount > 0) {
        is_additional = true
      }
    }

    return is_additional
  }

  get_additional_usage_price(){
    let price = 0 

    this.data.charge_periods_attributes.map(cp => {
      price = this.get_an_additional_usage_price(cp)
    })

    return price
  }

  get_an_additional_usage_price(cp){
    let price = 0

    if (this.data.charge_tier_type === 'N'){
      return price
    }

    /*
      Initially just dealing with two tier levels
      tier 1 used to indicate if there are any included values for the billing plan like 5 DNI numbers
      tier 2 used to indicate the usage price
    */
    if (cp.charge_period_tiers_attributes){
      // the first tier data is used to represent the included amount
      // so the low value and price are expected to be 0
      // get the last tier record. Assumption is that this represents the additional usage
      const additional_item_tier = cp.charge_period_tiers_attributes.slice(-1)[0]
      price = parseFloat(additional_item_tier.price)
    }
    
    return price
  }

  set_additional_usage_price(v){
    v = parseFloat(v)
    this.data.charge_periods_attributes.map(cp => {
      if (this.data.charge_tier_type !== 'N'){ 
        if (cp.charge_period_tiers_attributes) {
          // the first tier data is used to represent the included amount
          // so the low value and price are expected to be 0
          // get the last tier record. Assumption is that this represents the additional usage
          const additional_item_tier = cp.charge_period_tiers_attributes.slice(-1)[0]
          additional_item_tier.price = v
        }
      }
    })  
  }


  update_tier_high_value(v) {
    this.data.charge_periods_attributes[0].charge_period_tiers_attributes[0].tier_low_value = 0
    this.data.charge_periods_attributes[0].charge_period_tiers_attributes[0].tier_high_value = v
    this.data.charge_periods_attributes[0].charge_period_tiers_attributes[1].tier_low_value = v + 1  
  }

  update_date_range_value(start_v, end_v) {
    this.data.charge_periods_attributes[0].start_date = start_v
    this.data.charge_periods_attributes[0].end_date = end_v
  }

  add_charge_period() {
    // not allowed for this tier type
    if (this.data.charge_tier_type === 'N') { return }
    
    // first tier represents the included number of features
    // second tier represents the additional usage price
    const faker_tier_data = [{tier_low_value:0, tier_high_value:0, price:0}, {tier_low_value:0, tier_high_value:99999999.0000, price:0}]

    const todays_date = moment(new Date()).format("YYYY-MM-DD");
    // stop in 10 years should be enough
    const end_date = moment(new Date()).add(10,'y').format("YYYY-MM-DD")

    let unique_hash = Math.random().toString(36).substr(2,9);
    const fake_id = unique_hash
    const fake_charge_period = {id: fake_id, rate_schedule_charge_id: this.data.id, start_date: todays_date, end_date: end_date, price: 0,charge_period_tiers_attributes: faker_tier_data}

    this.data.charge_periods_attributes.push(fake_charge_period)
    console.log(this.data.charge_periods_attributes)
  }
}
